import React from 'react';
import YouTubeFacade from './YouTubeFacade';

const advantageData = [
  {
    id: 1,
    problem: {
      title: "The Imagination Gap",
      desc: "Customers struggle to visualize how furniture fits in their home, leading to hesitation.",
      mobileDesc: "Difficulty visualizing product fit leads to buyer hesitation.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )
    },
    solution: {
      title: "In-Room Augmented Reality",
      desc: "We bridge the gap with 99% accurate AR. Buyers place products in their room instantly to verify size and style.",
      mobileDesc: "Accurate AR lets buyers place products in their room instantly.",
    },
    impact: {
      value: "94%",
      label: "Higher Conversion",
      trend: "up",
      source: "Shopify Product Data"
    }
  },
  {
    id: 2,
    problem: {
      title: "Decision Paralysis",
      desc: "Uncertainty is the biggest killer of sales. Without visual clarity, high-ticket decision-making stalls.",
      mobileDesc: "Lack of visual clarity stalls high-ticket decision-making.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    solution: {
      title: "Absolute Visual Clarity",
      desc: "Photorealistic 4K visuals remove doubt, giving buyers the confidence to commit to the purchase.",
      mobileDesc: "Photorealistic 4K visuals give buyers confidence to commit.",
    },
    impact: {
      value: "11x",
      label: "Likely to Purchase",
      trend: "up",
      source: "Houzz UX Study"
    }
  },
  {
    id: 3,
    problem: {
      title: "Post-Purchase Regret",
      desc: "When products don't match expectations, it leads to costly returns and damaged brand loyalty.",
      mobileDesc: "Expectation gaps lead to costly returns and low loyalty.",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4 2 4 2z" />
        </svg>
      )
    },
    solution: {
      title: "Expectation Matching",
      desc: "What they see is exactly what they get. 3D visualization ensures the product matches the promise.",
      mobileDesc: "3D visuals ensure products match the promise exactly.",
    },
    impact: {
      value: "40%",
      label: "Reduction in Returns",
      trend: "down",
      source: "Forbes / Shopify"
    }
  }
];

const AdvantageCard: React.FC<{ data: typeof advantageData[0], index: number }> = ({ data, index }) => {
  const isUp = data.impact.trend === 'up';

  return (
    <div
      data-animation="fade-in-up"
      className={`stagger-${index + 1} group relative flex flex-col md:flex-row bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 hover:border-transparent transition-all duration-700 overflow-hidden isolate w-full shadow-xl shadow-slate-200/40 md:shadow-none`}
    >
      {/* Architectural Grain Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")` }}></div>

      {/* MOBILE COMPACT VIEW (Mockup Aligned) */}
      <div className="md:hidden relative z-10 flex items-start p-6 w-full gap-3">
        {/* Left Side: Content Bundle */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-4 h-4 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-xs flex-shrink-0 ${isUp ? 'text-[#FF5C00]' : 'text-[#003B5C]'}`}>
              {React.cloneElement(data.problem.icon as React.ReactElement, { className: 'w-2.5 h-2.5' })}
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest break-words leading-tight">
              {data.problem.title}
            </p>
          </div>

          <h4 className="text-lg font-black leading-[1.1] mb-2 tracking-tight text-[#021226]">
            {data.solution.title}
          </h4>

          <p className="text-[11px] text-slate-500 font-medium leading-relaxed opacity-90">
            {data.solution.mobileDesc}
          </p>
        </div>

        {/* Right Side: Impact Bundle */}
        <div className="flex flex-col items-center text-center min-w-[80px] border-l border-slate-50 pl-3 pt-1">
          <div className={`text-3xl font-black tracking-tighter leading-none mb-1 bg-clip-text text-transparent bg-gradient-to-br ${isUp ? 'from-[#FF5C00] via-[#FF8C4D] to-[#E65200]' : 'from-[#003B5C] via-[#005280] to-[#00253A]'}`}>
            {data.impact.value}
          </div>

          <div className={`text-[8px] font-black uppercase tracking-wider mb-3 leading-tight ${isUp ? 'text-[#FF5C00]' : 'text-[#003B5C]'}`}>
            {data.impact.label.split(' ').map((word, i) => (
              <span key={i} className="block">{word}</span>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100 shadow-sm">
            <div className={`w-1 h-1 rounded-full ${isUp ? 'bg-[#FF5C00]' : 'bg-[#003B5C]'}`}></div>
            <span className="text-[6px] font-black text-slate-400 uppercase tracking-widest">{data.impact.source}</span>
          </div>
        </div>
      </div>

      {/* DESKTOP ARCHITECTURAL VIEW (Hidden on Mobile) */}
      <div className="hidden md:flex w-full">
        {/* Progressive Glow Border (Pseudo-element) */}
        <div className="absolute inset-0 -z-10 rounded-[2.5rem] p-[1px] bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:from-[#003B5C]/30 group-hover:via-[#FF5C00]/30 group-hover:to-[#003B5C]/30 transition-all duration-1000 opacity-0 group-hover:opacity-100 mask-image:linear-gradient(white, white)"></div>

        {/* VISUAL BRIDGE: Connecting Gradient Line */}
        <div className="absolute top-1/2 left-[10%] right-[10%] h-px bg-gradient-to-r from-red-200 via-transparent to-blue-200 -translate-y-1/2 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 hidden md:block"></div>

        {/* 1. Problem (The Friction) - Left */}
        <div className="p-6 md:p-8 md:w-[28%] flex flex-col justify-center bg-[#FAFBFC] border-b md:border-b-0 md:border-r border-slate-100/60 relative group-hover:bg-white transition-colors duration-700 overflow-hidden">
          {/* Parallax Floating Accents (Left) */}
          <div className="absolute top-10 -left-10 w-24 h-24 bg-red-500/5 rounded-full blur-2xl animate-float-slow pointer-events-none"></div>

          <div className="flex items-center gap-3 mb-5 relative z-10">
            <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-red-500 group-hover:border-red-200 group-hover:bg-red-50 transition-all duration-500 shadow-sm relative">
              {/* Connector Dot */}
              <div className="absolute top-1/2 -right-4 w-8 h-px bg-red-200 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              {data.problem.icon}
            </div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] group-hover:text-red-400 transition-colors">Friction</p>
          </div>
          <div className="relative mb-3 z-10">
            <div className="absolute -left-4 top-0 w-1 h-full bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></div>
            <h4 className="text-xl font-black text-[#021226] leading-[1.2] tracking-tighter uppercase">{data.problem.title}</h4>
          </div>
          <p className="text-sm text-slate-500 font-medium leading-[1.6] opacity-80 group-hover:opacity-100 transition-all duration-500 relative z-10">
            <span className="hidden md:block">{data.problem.desc}</span>
            <span className="md:hidden">{data.problem.mobileDesc}</span>
          </p>
        </div>

        {/* 2. Impact Metric (The "Hero") - Middle */}
        <div className="p-6 md:p-8 md:w-[44%] flex flex-col items-center justify-center text-center relative overflow-hidden bg-white">
          {/* Architectural backdrop */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent"></div>
          <div className="absolute inset-y-8 left-1/2 -translate-x-1/2 w-48 h-48 bg-radial-gradient from-blue-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>

          {/* TRANSFORMATION ARROW (Background) */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-10 pointer-events-none transition-opacity duration-500 z-0">
            <div className="w-[80%] h-32 bg-gradient-to-r from-red-50/0 via-slate-100/50 to-blue-50/0 rounded-full blur-3xl transform -skew-x-12"></div>
            <svg className="absolute w-64 h-64 text-slate-50 opacity-100 transform -translate-x-4 group-hover:translate-x-0 transition-transform duration-1000" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.5" d="M13 5l7 7-7 7M5 12h15" />
            </svg>
          </div>

          {/* Parallax Geometric Accents (Center) */}
          <div className="absolute top-4 right-10 w-2 h-2 rounded-full bg-[#003B5C]/10 animate-float-medium delay-100 pointer-events-none"></div>
          <div className="absolute bottom-10 left-10 w-3 h-3 border border-[#FF5C00]/10 rotate-45 animate-float-fast delay-300 pointer-events-none"></div>

          <div className="relative z-10 transition-all duration-700 group-hover:scale-[1.05]">
            <div className={`text-5xl md:text-6xl font-black tracking-tighter leading-none mb-4 bg-clip-text text-transparent bg-gradient-to-br ${isUp ? 'from-[#FF5C00] via-[#FF8C4D] to-[#E65200]' : 'from-[#003B5C] via-[#005280] to-[#00253A]'} drop-shadow-[0_10px_10px_rgba(0,0,0,0.05)]`}>
              {data.impact.value}
            </div>

            <div className="flex items-center justify-center gap-3 mb-5">
              <div className={`h-[1px] w-8 ${isUp ? 'bg-[#FF5C00]/20' : 'bg-[#003B5C]/20'} group-hover:w-16 transition-all duration-700`}></div>
              <span className={`text-[12px] md:text-lg font-black uppercase tracking-wider ${isUp ? 'text-[#FF5C00]' : 'text-[#003B5C]'} opacity-100`}>
                {data.impact.label}
              </span>
              <div className={`h-[1px] w-8 ${isUp ? 'bg-[#FF5C00]/20' : 'bg-[#003B5C]/20'} group-hover:w-16 transition-all duration-700`}></div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-xl bg-slate-50/80 border border-slate-100 backdrop-blur-md group-hover:bg-white group-hover:border-slate-200 group-hover:shadow-lg group-hover:shadow-slate-200/50 transition-all duration-700">
              <div className={`w-1.5 h-1.5 rounded-full ${isUp ? 'bg-[#FF5C00] animate-pulse shadow-[0_0_8px_#FF5C00]' : 'bg-[#003B5C]'}`}></div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">{data.impact.source}</span>
            </div>
          </div>
        </div>

        {/* 3. Solution (The Advantage) - Right */}
        <div className="p-6 md:p-8 md:w-[28%] flex flex-col justify-center bg-[#FAFBFC] border-t md:border-t-0 md:border-l border-slate-100/60 relative group-hover:bg-white transition-colors duration-700 overflow-hidden">
          {/* Parallax Floating Accents (Right) */}
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[#003B5C]/5 rounded-full blur-3xl animate-float-slow delay-500 pointer-events-none"></div>

          <div className="flex items-center gap-2 mb-5 text-[#003B5C]/30 group-hover:text-[#003B5C] transition-all duration-700 relative z-10">
            {/* Connector Dot - Receiving End */}
            <div className="absolute top-1/2 -left-4 w-8 h-px bg-[#003B5C]/20 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="w-5 h-5 rounded-full border border-current flex items-center justify-center">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.4em]">Advantage</span>
          </div>
          <div className="relative mb-3 z-10">
            <div className="absolute -left-4 top-0 w-1 h-full bg-[#003B5C]/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"></div>
            <h4 className="text-xl font-black text-[#021226] leading-[1.2] tracking-tighter uppercase">{data.solution.title}</h4>
          </div>
          <p className="text-sm text-slate-500 font-medium leading-[1.6] opacity-80 group-hover:opacity-100 transition-all duration-500 relative z-10">
            <span className="hidden md:block">{data.solution.desc}</span>
            <span className="md:hidden">{data.solution.mobileDesc}</span>
          </p>

          {/* Floating Accent Detail */}
          <div className="absolute top-0 right-0 w-16 h-16 opacity-[0.02] group-hover:opacity-[0.08] transition-opacity duration-1000 rotate-45 translate-x-8 -translate-y-8 z-0">
            <div className="w-full h-full border-[3px] border-[#003B5C]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const About: React.FC = () => {
  const scrollToId = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="relative pt-20 pb-8 md:pt-32 md:pb-12 bg-white overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-slate-50 opacity-30 skew-x-[-15deg] translate-x-1/2 pointer-events-none"></div>

      <div className="container-xl relative z-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 md:mb-24 px-4">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#FF5C00]/10 text-[#FF5C00] text-[10px] font-black tracking-[0.3em] uppercase mb-6 border border-[#FF5C00]/20">
            The Retail Evolution
          </span>
          <h2 data-animation="fade-in-up" className="text-2xl md:text-4xl lg:text-5xl font-black text-[#021226] leading-[1.1] tracking-tight">
            Immersive Product <br className="hidden md:block" />
            <span className="text-[#FF5C00]">Experiences That Sell</span>
          </h2>
          <p data-animation="fade-in-up" className="mt-6 text-base md:text-lg text-slate-500 stagger-1 font-medium max-w-2xl mx-auto leading-relaxed">
            We build photorealistic 3D, AR, and AI solutions that remove buyer doubt. Our mission is to transform the way furniture is presented, customized, and sold globally.
          </p>
        </div>

        {/* Bio Section with Video */}
        <div className="grid lg:grid-cols-2 gap-12 xl:gap-24 items-center mb-20 md:mb-32">
          <div data-animation="fade-in-left" className="stagger-1 space-y-8">
            <h3 className="text-xl md:text-3xl font-black text-[#021226] leading-tight">
              Bridging the gap between <br />
              <span className="text-[#003B5C]">Product</span> & <span className="text-[#FF5C00]">Customer</span>
            </h3>

            <div className="space-y-4">
              <p className="text-base md:text-lg text-slate-600 leading-relaxed font-medium">
                Founded by IIM Calcutta alumnus <span className="text-[#021226] font-bold">Shreedhar Shroff</span>, OmniView Labs empowers furniture retailers to prevent "lost sales" through interactive visualization. We believe that buying high-end furniture shouldn't be a game of guesswork.
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed font-medium">
                Our core technology transforms static product catalogs into an interactive canvas. By leveraging the power of 3D rendering and Augmented Reality, we provide customers with the absolute clarity they need to make confident, high-ticket purchase decisions in seconds.
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed font-medium">
                Today, our solutions power over 40 retail locations across India, helping brands reduce return rates by 30% and boost average order value by showcasing the full potential of their customization engines.
              </p>
            </div>

            <button
              onClick={(e) => scrollToId(e, 'advantage-section')}
              className="group flex items-center gap-3 text-[#FF5C00] font-black text-xs uppercase tracking-[0.2em]"
            >
              See how we solve it
              <div className="w-8 h-8 rounded-full bg-[#FF5C00]/10 flex items-center justify-center group-hover:bg-[#FF5C00] group-hover:text-white transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </div>
            </button>
          </div>

          <div data-animation="fade-in-right" className="relative stagger-2">
            <div className="aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl border-[8px] border-white bg-black">
              <YouTubeFacade
                videoId="j1IL7yIBV-A"
                title="OmniView Labs Solution Demo"
              />
            </div>
          </div>
        </div>

        {/* Unified Advantage Section */}
        <div id="advantage-section" className="relative">
          <div className="text-center mb-12 px-4">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] mb-4">From Friction to Flow</h4>
            <h2 className="text-3xl md:text-5xl font-black text-[#021226] mb-4 tracking-tight">
              The <span className="text-[#003B5C]">OmniView Advantage</span>
            </h2>
            <p className="text-slate-500 text-sm md:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
              Transforming the furniture buying experience by eliminating common purchasing frictions and maximizing customer satisfaction.
            </p>
          </div>

          <div className="relative w-full max-w-6xl mx-auto">
            <div className="flex flex-col gap-4 md:gap-3 px-4 md:px-0">
              {advantageData.map((data, index) => (
                <AdvantageCard key={data.id} data={data} index={index} />
              ))}
            </div>
          </div>
        </div>

        {/* New Interactive Demo CTA Section */}
        <div className="mt-20 relative z-10 mx-auto max-w-4xl px-4 md:px-0" data-animation="zoom-in">
          <div className="relative rounded-[2rem] bg-[#021226] overflow-hidden shadow-2xl shadow-blue-900/30 group border border-white/5">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#003B5C]/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:bg-[#003B5C]/50 transition-colors duration-700"></div>
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-[#FF5C00]/20 rounded-full blur-[60px] translate-y-1/2 -translate-x-1/2 pointer-events-none group-hover:bg-[#FF5C00]/30 transition-colors duration-700"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 mix-blend-overlay"></div>

            <div className="relative z-10 px-8 py-10 md:px-12 md:py-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
              <div className="flex-1 max-w-xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[#FF5C00] text-[9px] font-black uppercase tracking-widest mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00] animate-pulse shadow-[0_0_10px_#FF5C00]"></span>
                  Modern Retail Solutions
                </div>
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-[1.1] mb-3 tracking-tight">
                  Ready to transform your <br />
                  <span className="text-[#FF5C00]">Retail Experience?</span>
                </h3>
                <p className="text-slate-400 text-sm md:text-base font-medium leading-relaxed max-w-md">
                  From agile storefronts to unified furniture ecosystems. Discover the tools built for your growth.
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col items-center gap-3">
                <a
                  href="#products"
                  className="group/btn relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#021226] rounded-xl font-black text-xs uppercase tracking-widest shadow-[0_0_30px_rgba(255,255,255,0.1)] hover:shadow-[0_0_50px_rgba(255,255,255,0.2)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span className="relative z-10">View Solutions</span>
                  <div className="relative z-10 w-6 h-6 rounded-full bg-[#021226] text-white flex items-center justify-center group-hover/btn:bg-[#FF5C00] transition-colors duration-300">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </div>
                  {/* Button Sheen */}
                  <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-slate-200/50 to-transparent -translate-x-full group-hover/btn:animate-shimmer"></div>
                  </div>
                </a>
                <div className="flex items-center gap-1.5 opacity-70">
                  <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                    Instant Load • No Lag
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;